import { z } from "zod";
import {
  teamMemberPaymentSettingsSchema,
  teamPageResponseSchema,
  reorderTeamPageSchema,
  workingSinceSchema,
  updateTeamMemberPaymentSettingsSchema,
  dashboardSectionSchema,
  staffShortfallResponseSchema,
  type DashboardSection,
  type TeamMemberPaymentSettings,
  type UpdateTeamMemberPaymentSettingsRequest,
} from "@openings/api-contract";
import { BaseHttpClient, baseOk, type ApiOk } from "./base";

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const teamMemberSchema = z.object({
  id: z.string(),
  salonId: z.string(),
  email: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  profilePhotoUrl: z.string().nullable(),
  profileBio: z.string().nullable(),
  workingSince: workingSinceSchema.nullable().optional(),
  teamPageOrder: z.number().int().nullable().optional(),
  role: z.enum(["MANAGER", "STAFF"]),
  isActive: z.boolean(),
  canEditSchedule: z.boolean(),
  canEditServices: z.boolean(),
  canManageAppointments: z.boolean(),
  canViewReports: z.boolean(),
  canManageClients: z.boolean(),
  // Older API deployments predate these fields — keep them optional so a
  // dashboard build never hard-fails validation against a lagging API.
  canEditProfile: z.boolean().optional(),
  canViewCustomerContact: z.boolean().optional(),
  canIssueRefunds: z.boolean().optional(),
  // Product-commerce permissions (Aug 2026) — optional for the same reason.
  canSellProducts: z.boolean().optional(),
  canManageProducts: z.boolean().optional(),
  canAdjustProductInventory: z.boolean().optional(),
  canDiscountProductSales: z.boolean().optional(),
  canAssignProductSeller: z.boolean().optional(),
  canViewProductCosts: z.boolean().optional(),
  managerSections: z.array(dashboardSectionSchema).optional(),
  // Business admin (Oct 2026); optional while older API builds age out.
  isAdmin: z.boolean().optional(),
  workType: z.enum(["EMPLOYEE", "CONTRACTOR"]).nullable(),
  checkoutPreference: z
    .enum(["NONE", "MANUAL", "TAP_TO_PAY", "OPENINGS_POS"])
    .nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

const listSchema = baseOk(z.object({ members: z.array(teamMemberSchema) }));
const singleSchema = baseOk(z.object({ member: teamMemberSchema }));
const deleteSchema = baseOk(z.object({ success: z.literal(true) }));
const inviteSchema = baseOk(z.object({ sent: z.literal(true) }));

const inviteLogSchema = z.object({
  id: z.string(),
  email: z.string(),
  status: z.enum(["PENDING", "ACCEPTED", "DECLINED"]),
  sentAt: z.coerce.date(),
  acceptedAt: z.coerce.date().nullable(),
});

const sessionLogSchema = z.object({
  id: z.string(),
  createdAt: z.coerce.date(),
  revokedAt: z.coerce.date().nullable(),
  userAgent: z.string().nullable(),
  ipAddress: z.string().nullable(),
  expiresAt: z.coerce.date(),
});

const inviteHistorySchema = baseOk(
  z.object({
    invites: z.array(inviteLogSchema),
    sessions: z.array(sessionLogSchema),
  }),
);

const paymentSettingsSchema = baseOk(
  z.object({ settings: teamMemberPaymentSettingsSchema }),
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type TeamMemberDTO = z.infer<typeof teamMemberSchema>;
export type ListTeamMembersResult = ApiOk<{ members: TeamMemberDTO[] }>;
export type GetTeamMemberResult = ApiOk<{ member: TeamMemberDTO }>;
export type DeleteTeamMemberResult = ApiOk<{ success: true }>;
export type ResendInviteResult = ApiOk<{ sent: true }>;
export type InviteLogEntry = z.infer<typeof inviteLogSchema>;
export type SessionLogEntry = z.infer<typeof sessionLogSchema>;
export type InviteHistoryResult = ApiOk<{
  invites: InviteLogEntry[];
  sessions: SessionLogEntry[];
}>;

export type GetPaymentSettingsResult = ApiOk<{
  settings: TeamMemberPaymentSettings;
}>;
export type UpdatePaymentSettingsResult = GetPaymentSettingsResult;
export type UpdatePaymentSettingsInput = UpdateTeamMemberPaymentSettingsRequest;

export interface CreateTeamMemberInput {
  email: string;
  name: string;
  phone?: string;
  role?: "MANAGER" | "STAFF";
  workType?: "EMPLOYEE" | "CONTRACTOR";
  canEditSchedule?: boolean;
  canEditServices?: boolean;
  canManageAppointments?: boolean;
  canViewReports?: boolean;
  canManageClients?: boolean;
  canSellProducts?: boolean;
  canManageProducts?: boolean;
  canAdjustProductInventory?: boolean;
  canDiscountProductSales?: boolean;
  canAssignProductSeller?: boolean;
  canViewProductCosts?: boolean;
  managerSections?: DashboardSection[];
  /** Business admin — owner only, and only with role MANAGER. */
  isAdmin?: boolean;
  sendInvite?: boolean;
}

export interface UpdateTeamMemberInput {
  name?: string;
  phone?: string | null;
  role?: "MANAGER" | "STAFF";
  profilePhotoUrl?: string | null;
  profileBio?: string | null;
  workingSince?: string | null;
  isActive?: boolean;
  workType?: "EMPLOYEE" | "CONTRACTOR" | null;
  checkoutPreference?: "NONE" | "MANUAL" | "TAP_TO_PAY" | "OPENINGS_POS" | null;
  canEditSchedule?: boolean;
  canEditServices?: boolean;
  canManageAppointments?: boolean;
  canViewReports?: boolean;
  canManageClients?: boolean;
  canEditProfile?: boolean;
  canViewCustomerContact?: boolean;
  canIssueRefunds?: boolean;
  canSellProducts?: boolean;
  canManageProducts?: boolean;
  canAdjustProductInventory?: boolean;
  canDiscountProductSales?: boolean;
  canAssignProductSeller?: boolean;
  canViewProductCosts?: boolean;
  /**
   * Dashboard sections granted to a MANAGER. The API validates against the
   * grantable enum — owner-only surfaces are not expressible here.
   */
  managerSections?: DashboardSection[];
  /**
   * Business admin (plans/business-admin-role-spec.md). Only the owner may
   * send it, and only for a MANAGER; a demotion to STAFF clears it.
   */
  isAdmin?: boolean;
}

// ---------------------------------------------------------------------------
// Client
// ---------------------------------------------------------------------------

export class TeamMembersClient extends BaseHttpClient {
  private memberPath(salonId: string, memberId?: string): string {
    const base = `/v1/salons/${encodeURIComponent(salonId)}/team-members`;
    return memberId ? `${base}/${encodeURIComponent(memberId)}` : base;
  }

  listPublic(
    salonId: string,
  ): Promise<ApiOk<z.infer<typeof teamPageResponseSchema>>> {
    return this.get(
      `/v1/salons/${encodeURIComponent(salonId)}/team-page`,
      baseOk(teamPageResponseSchema),
    );
  }

  reorderTeamPage(
    salonId: string,
    memberIds: string[],
  ): Promise<ApiOk<{ success: true }>> {
    return this.put(
      `/v1/salons/${encodeURIComponent(salonId)}/team-page-order`,
      reorderTeamPageSchema.parse({ memberIds }),
      deleteSchema,
    );
  }

  list(salonId: string): Promise<ListTeamMembersResult> {
    return this.get(this.memberPath(salonId), listSchema);
  }

  getMember(salonId: string, memberId: string): Promise<GetTeamMemberResult> {
    return this.get(this.memberPath(salonId, memberId), singleSchema);
  }

  create(
    salonId: string,
    input: CreateTeamMemberInput,
  ): Promise<GetTeamMemberResult> {
    return this.post(this.memberPath(salonId), input, singleSchema);
  }

  update(
    salonId: string,
    memberId: string,
    input: UpdateTeamMemberInput,
  ): Promise<GetTeamMemberResult> {
    return this.patch(this.memberPath(salonId, memberId), input, singleSchema);
  }

  /** Refund shortfall a departing member would leave (owner-only). */
  getShortfall(
    salonId: string,
    memberId: string,
  ): Promise<ApiOk<z.infer<typeof staffShortfallResponseSchema>>> {
    return this.get(
      `${this.memberPath(salonId, memberId)}/shortfall`,
      baseOk(staffShortfallResponseSchema),
    );
  }

  remove(salonId: string, memberId: string): Promise<DeleteTeamMemberResult> {
    return this.del(this.memberPath(salonId, memberId), deleteSchema);
  }

  resendInvite(salonId: string, memberId: string): Promise<ResendInviteResult> {
    return this.post(
      `${this.memberPath(salonId, memberId)}/invite`,
      {},
      inviteSchema,
    );
  }

  getInviteHistory(
    salonId: string,
    memberId: string,
  ): Promise<InviteHistoryResult> {
    return this.get(
      `${this.memberPath(salonId, memberId)}/invites`,
      inviteHistorySchema,
    );
  }

  getPaymentSettings(
    salonId: string,
    memberId: string,
  ): Promise<GetPaymentSettingsResult> {
    return this.get(
      `${this.memberPath(salonId, memberId)}/payment-settings`,
      paymentSettingsSchema,
    );
  }

  updatePaymentSettings(
    salonId: string,
    memberId: string,
    input: UpdatePaymentSettingsInput,
  ): Promise<UpdatePaymentSettingsResult> {
    // Validate at the boundary so callers get clear errors before the
    // round-trip to the server.
    const parsed = updateTeamMemberPaymentSettingsSchema.parse(input);
    return this.put(
      `${this.memberPath(salonId, memberId)}/payment-settings`,
      parsed,
      paymentSettingsSchema,
    );
  }
}
